import { transformMediaUrl } from '@/lib/media-url-transformer';
import {
    CANCELLATION_TERMS_FIELD_KEY,
    CANCELLATION_TERMS_SECTION_ID,
    hasMeaningfulHtml,
    OTHER_DESCRIPTIONS_GROUP_KEY,
    slugifySectionId,
} from '@/utils/productOtherDescriptionsUtils';

export interface YachtFeature {
    key: string;
    label: string;
    value: string | number | boolean | null;
    fieldType: string;
}

export interface EquipmentCategory {
    key: string;
    title: string;
    items: string[];
}

export interface YachtCondition {
    key: string;
    label: string;
    value: string | boolean;
}

export interface YachtMonthlyPrice {
    month: number;
    monthName: string;
    monthNameFull: string;
    price: number;
    currency: string;
    isAvailable: boolean;
    note?: string;
}

export interface Service {
    title: string;
    description: string;
}

export class ProductYachtDataService {
    static parseYachtFeatures(metas: any, fields: any): YachtFeature[] {
        if (!metas || !fields) return [];

        const features: YachtFeature[] = [];

        Object.keys(fields).forEach((fieldKey) => {
            const field = fields[fieldKey];
            if (field &&
                field.tab_group_key === 'features' &&
                fieldKey !== 'tab_section_features' &&
                field.has_data) {
                const value = metas[fieldKey];
                const shouldInclude = field.field_type === 'boolean'
                    ? (value !== null && value !== undefined)
                    : (value !== null && value !== undefined && value !== '');
                if (shouldInclude) {
                    let displayValue: string | number | boolean = value;
                    if (field.field_type === 'boolean') {
                        displayValue = Boolean(value);
                    } else if (field.field_type === 'number') {
                        const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
                        displayValue = isNaN(numValue) ? value : numValue;
                    } else if (field.field_type === 'select' && field.field_options) {
                        const options = typeof field.field_options === 'string'
                            ? JSON.parse(field.field_options)
                            : field.field_options;
                        if (options && options.options && options.options[value]) {
                            const optionLabel = options.options[value];
                            if (typeof optionLabel === 'object') {
                                displayValue = optionLabel['en'] || optionLabel['tr'] || value;
                            } else {
                                displayValue = optionLabel || value;
                            }
                        }
                    }
                    features.push({
                        key: fieldKey,
                        label: field.field_title || fieldKey,
                        value: displayValue,
                        fieldType: field.field_type || 'text',
                    });
                }
            }
        });

        return features.sort((a, b) => {
            const orderA = fields[a.key]?.sort_order ?? 999;
            const orderB = fields[b.key]?.sort_order ?? 999;
            return orderA - orderB;
        });
    }

    static parseEquipment(metas: any, fields: any, language: string = 'en'): EquipmentCategory[] {
        if (!metas || !fields) return [];

        const equipmentCategories: EquipmentCategory[] = [];
        const categoryMap: Record<string, string> = {
            'navigation_equipment': 'Navigation Equipment',
            'outdoor_equipment': 'Outdoor Equipment',
            'kitchen_equipment': 'Kitchen Equipment',
            'leisure_equipment': 'Leisure Activities',
            'included_equipment': 'Included Equipment',
            'safety_equipment': 'Safety Equipment',
            'extra_comfort': 'Extra Comfort',
        };

        Object.entries(categoryMap).forEach(([fieldKey, defaultTitle]) => {
            const field = fields[fieldKey];
            const value = metas[fieldKey];

            if (field && field.has_data && value) {
                let itemKeys: string[] = [];

                if (Array.isArray(value)) {
                    itemKeys = value.filter((item): item is string => typeof item === 'string' && item.trim() !== '');
                } else if (typeof value === 'string') {
                    try {
                        const parsed = JSON.parse(value);
                        if (Array.isArray(parsed)) {
                            itemKeys = parsed.filter((item): item is string => typeof item === 'string' && item.trim() !== '');
                        }
                    } catch {
                        itemKeys = value.split(',').map((item: string) => item.trim()).filter(Boolean);
                    }
                }

                if (itemKeys.length > 0) {
                    // Ekipman item'larını çevir
                    const translatedItems: string[] = itemKeys.map((itemKey: string) => {
                        // field_options varsa çeviriyi oradan al
                        if (field.field_options && typeof field.field_options === 'object') {
                            const option = field.field_options[itemKey];
                            if (option && typeof option === 'object') {
                                // Çok dilli çeviri objesi
                                return option[language] || option['en'] || option['tr'] || itemKey;
                            } else if (typeof option === 'string') {
                                // Tek dilli string
                                return option;
                            }
                        }
                        // Fallback: key'i direkt döndür
                        return itemKey;
                    });

                    equipmentCategories.push({
                        key: fieldKey,
                        title: field.field_title || defaultTitle,
                        items: translatedItems,
                    });
                }
            }
        });

        return equipmentCategories.sort((a, b) => {
            const orderA = fields[a.key]?.sort_order || 999;
            const orderB = fields[b.key]?.sort_order || 999;
            return orderA - orderB;
        });
    }

    static parseConditions(metas: any, fields: any): YachtCondition[] {
        if (!metas || !fields) return [];

        const conditions: YachtCondition[] = [];
        const conditionFieldKeys = [
            'smoking_allowed',
            'pets_allowed',
            'party_allowed',
            'child_friendly',
        ];

        conditionFieldKeys.forEach((fieldKey) => {
            const field = fields[fieldKey];
            const value = metas[fieldKey];

            if (field && field.has_data && value !== null && value !== undefined) {
                const displayValue = typeof value === 'boolean' 
                    ? (value ? 'Yes' : 'No')
                    : String(value);

                conditions.push({
                    key: fieldKey,
                    label: field.field_title || fieldKey,
                    value: displayValue,
                });
            }
        });

        return conditions.sort((a, b) => {
            const orderA = fields[a.key]?.sort_order || 999;
            const orderB = fields[b.key]?.sort_order || 999;
            return orderA - orderB;
        });
    }

    static parseCheckInOut(metas: any, fields: any): { checkIn: string | null; checkOut: string | null } {
        if (!metas || !fields) {
            return { checkIn: null, checkOut: null };
        }

        const checkInField = fields.check_in_time;
        const checkOutField = fields.check_out_time;

        return {
            checkIn: checkInField?.has_data && metas.check_in_time 
                ? String(metas.check_in_time) 
                : null,
            checkOut: checkOutField?.has_data && metas.check_out_time 
                ? String(metas.check_out_time) 
                : null,
        };
    }

    static parseServices(metas: any, fields?: any): { included: Service[]; excluded: Service[]; optional: Service[] } {
        const included: Service[] = [];
        const excluded: Service[] = [];
        const optional: Service[] = [];

        // Helper function to parse and normalize services data
        const parseServicesData = (data: any): any[] => {
            if (!data) return [];
            
            // If it's a JSON string, parse it
            if (typeof data === 'string') {
                try {
                    const parsed = JSON.parse(data);
                    return Array.isArray(parsed) ? parsed : [];
                } catch {
                    // If parsing fails, treat as single string
                    return [data];
                }
            }
            
            // If it's already an array, return it
            if (Array.isArray(data)) {
                return data;
            }
            
            // If it's an object, wrap it in an array
            if (typeof data === 'object' && data !== null) {
                return [data];
            }
            
            return [];
        };

        // Helper function to extract service data from various formats
        const extractService = (service: any): Service | null => {
            if (!service) return null;
            
            // If it's a string, use it as title
            if (typeof service === 'string') {
                return { title: service.trim(), description: '' };
            }
            
            // If it's an object, extract title and description
            if (typeof service === 'object' && service !== null) {
                const title = service.title || service.name || service.label || '';
                const description = service.description || service.desc || '';
                
                if (title || description) {
                    return { 
                        title: typeof title === 'string' ? title.trim() : '', 
                        description: typeof description === 'string' ? description.trim() : '' 
                    };
                }
            }
            
            return null;
        };

        // Parse included_services - array format (repeater) veya string format (rich_text için boş array döndür, çünkü rich_text parseTabFields'da işleniyor)
        const includedServicesData = metas?.included_services;
        if (includedServicesData) {
            // Eğer string ise (rich_text), array formatı değil, bu durumda parseTabFields'da işlenecek
            // Sadece array formatındaki verileri işle
            if (Array.isArray(includedServicesData)) {
                const parsed = parseServicesData(includedServicesData);
                parsed.forEach((service: any) => {
                    const extracted = extractService(service);
                    if (extracted) {
                        included.push(extracted);
                    }
                });
            }
        }

        // Parse excluded_services - array format (repeater) veya string format (rich_text için boş array döndür)
        const excludedServicesData = metas?.excluded_services;
        if (excludedServicesData) {
            // Eğer string ise (rich_text), array formatı değil, bu durumda parseTabFields'da işlenecek
            // Sadece array formatındaki verileri işle
            if (Array.isArray(excludedServicesData)) {
                const parsed = parseServicesData(excludedServicesData);
                parsed.forEach((service: any) => {
                    const extracted = extractService(service);
                    if (extracted) {
                        excluded.push(extracted);
                    }
                });
            }
        }

        // Parse optional_services - array format
        const optionalServicesData = metas?.optional_services;
        if (optionalServicesData && Array.isArray(optionalServicesData)) {
            const parsed = parseServicesData(optionalServicesData);
            parsed.forEach((service: any) => {
                const extracted = extractService(service);
                if (extracted) {
                    optional.push(extracted);
                }
            });
        }

        return { included, excluded, optional };
    }

    static parseMonthlyPrices(prices: any[], currency: string = 'EUR'): YachtMonthlyPrice[] {
        if (!Array.isArray(prices) || prices.length === 0) return [];

        const monthNames: Record<number, { short: string; full: string; fullTR: string }> = {
            1: { short: 'Jan', full: 'January', fullTR: 'Ocak' },
            2: { short: 'Feb', full: 'February', fullTR: 'Şubat' },
            3: { short: 'Mar', full: 'March', fullTR: 'Mart' },
            4: { short: 'Apr', full: 'April', fullTR: 'Nisan' },
            5: { short: 'May', full: 'May', fullTR: 'Mayıs' },
            6: { short: 'Jun', full: 'June', fullTR: 'Haziran' },
            7: { short: 'Jul', full: 'July', fullTR: 'Temmuz' },
            8: { short: 'Aug', full: 'August', fullTR: 'Ağustos' },
            9: { short: 'Sep', full: 'September', fullTR: 'Eylül' },
            10: { short: 'Oct', full: 'October', fullTR: 'Ekim' },
            11: { short: 'Nov', full: 'November', fullTR: 'Kasım' },
            12: { short: 'Dec', full: 'December', fullTR: 'Aralık' },
        };

        const validPrices = prices
            .filter((price) => {
                const hasMonth = price.month !== null && price.month !== undefined;
                const hasPrice = price.base_price !== null && price.base_price !== undefined && parseFloat(String(price.base_price)) > 0;
                const isActive = price.is_active !== false && price.is_active !== 0;
                return hasMonth && hasPrice && isActive;
            })
            .map((price): YachtMonthlyPrice | null => {
                const month = parseInt(String(price.month));
                if (month < 1 || month > 12) return null;
                
                const monthData = monthNames[month] || { short: `M${month}`, full: `Month ${month}`, fullTR: `Ay ${month}` };
                const basePrice = parseFloat(String(price.base_price)) || 0;

                return {
                    month: month,
                    monthName: monthData.short,
                    monthNameFull: monthData.full,
                    price: basePrice,
                    currency: currency,
                    isAvailable: basePrice > 0,
                    note: price.note || undefined,
                };
            })
            .filter((price): price is YachtMonthlyPrice => price !== null)
            .sort((a, b) => a.month - b.month);

        return validPrices;
    }

    static parseInformation(metas: any, fields: any, language: string = 'en'): { content: string | null; fieldTitle: string | null } {
        if (!metas || !fields) {
            return { content: null, fieldTitle: null };
        }

        const field = fields['information'];
        const value = metas['information'];

        if (!field || !field.has_data || !value || (typeof value === 'string' && value.trim() === '')) {
            return { content: null, fieldTitle: null };
        }

        // Field title'ı dil bazlı al
        let fieldTitle = 'Information';
        if (field.field_titles) {
            const titles = typeof field.field_titles === 'string' 
                ? JSON.parse(field.field_titles) 
                : field.field_titles;
            fieldTitle = titles[language] || titles['en'] || titles['tr'] || 'Information';
        }

        return {
            content: typeof value === 'string' ? value : String(value),
            fieldTitle,
        };
    }

    static parseServicesContent(metas: any, fields: any, language: string = 'en'): { 
        includedContent: string | null; 
        excludedContent: string | null;
        includedTitle: string | null;
        excludedTitle: string | null;
    } {
        if (!metas || !fields) {
            return { includedContent: null, excludedContent: null, includedTitle: null, excludedTitle: null };
        }

        const includedField = fields['included_services'];
        const excludedField = fields['excluded_services'];
        const includedValue = metas['included_services'];
        const excludedValue = metas['excluded_services'];

        let includedContent: string | null = null;
        let includedTitle: string | null = null;
        if (includedField && includedValue) {
            if ((includedField.field_type === 'rich_text' || typeof includedValue === 'string') && 
                typeof includedValue === 'string' && includedValue.trim() !== '') {
                includedContent = includedValue;
                
                if (includedField.field_titles) {
                    const titles = typeof includedField.field_titles === 'string' 
                        ? JSON.parse(includedField.field_titles) 
                        : includedField.field_titles;
                    includedTitle = titles[language] || titles['en'] || titles['tr'] || includedField.field_title || 'Included Services';
                } else {
                    includedTitle = includedField.field_title || 'Included Services';
                }
            }
        }

        let excludedContent: string | null = null;
        let excludedTitle: string | null = null;
        if (excludedField && excludedValue) {
            if ((excludedField.field_type === 'rich_text' || typeof excludedValue === 'string') && 
                typeof excludedValue === 'string' && excludedValue.trim() !== '') {
                excludedContent = excludedValue;
                
                if (excludedField.field_titles) {
                    const titles = typeof excludedField.field_titles === 'string' 
                        ? JSON.parse(excludedField.field_titles) 
                        : excludedField.field_titles;
                    excludedTitle = titles[language] || titles['en'] || titles['tr'] || excludedField.field_title || 'Excluded Services';
                } else {
                    excludedTitle = excludedField.field_title || 'Excluded Services';
                }
            }
        }

        return { includedContent, excludedContent, includedTitle, excludedTitle };
    }

    static parseMarinaInfo(metas: any, fields: any): { name: string | null; location: string | null; description: string | null } {
        if (!metas || !fields) {
            return { name: null, location: null, description: null };
        }

        const marinaField = fields.yacht_marina;
        const marinaValue = metas.yacht_marina;

        let marinaName: string | null = null;
        if (marinaField?.has_data && marinaValue) {
            if (Array.isArray(marinaValue)) {
                marinaName = marinaValue[0] || null;
            } else if (typeof marinaValue === 'string') {
                try {
                    const parsed = JSON.parse(marinaValue);
                    marinaName = Array.isArray(parsed) ? parsed[0] : parsed;
                } catch {
                    marinaName = marinaValue;
                }
            }
        }

        return {
            name: marinaName,
            location: metas.marina_location || null,
            description: metas.marina_description || null,
        };
    }

    static parseTabFields(metas: any, fields: any, language: string = 'en'): Array<{
        tabName: string;
        sectionId: string;
        groupKey?: string;
        fields: Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
        }>;
    }> {
        if (!metas || !fields) {
            return [];
        }

        const result: Array<{
            tabName: string;
            sectionId: string;
            groupKey?: string;
            fields: Array<{
                fieldKey: string;
                fieldTitle: string;
                content: string;
            }>;
        }> = [];

        // 0. cancellation_terms – doluysa ayrı bölüm (HTML)
        const cancellationValue = metas[CANCELLATION_TERMS_FIELD_KEY];
        if (hasMeaningfulHtml(cancellationValue)) {
            const cancellationField = fields[CANCELLATION_TERMS_FIELD_KEY];
            const fieldTitle = cancellationField?.field_title || 'Cancellation Terms';
            result.push({
                tabName: fieldTitle,
                sectionId: CANCELLATION_TERMS_SECTION_ID,
                groupKey: OTHER_DESCRIPTIONS_GROUP_KEY,
                fields: [{
                    fieldKey: CANCELLATION_TERMS_FIELD_KEY,
                    fieldTitle,
                    content: typeof cancellationValue === 'string' ? cancellationValue : String(cancellationValue),
                }],
            });
        }

// 1. yacht_tabs array field'ını parse et (Diğer Açıklamalar – menüde dropdown)
        const yachtTabs = metas['yacht_tabs'];
        if (yachtTabs && Array.isArray(yachtTabs) && yachtTabs.length > 0) {
            yachtTabs.forEach((tabItem: any, index: number) => {
                const title = tabItem.title || tabItem.name || `Tab ${index + 1}`;
                const content = tabItem.content || tabItem.description || '';

                if (!hasMeaningfulHtml(content)) {
                    return;
                }

                const sectionId = slugifySectionId(title, `yacht-tab-${index}`);

                result.push({
                    tabName: title,
                    sectionId,
                    groupKey: OTHER_DESCRIPTIONS_GROUP_KEY,
                    fields: [{
                        fieldKey: 'yacht_tabs',
                        fieldTitle: title,
                        content: typeof content === 'string' ? content : String(content),
                    }],
                });
            });
        }

        // 1b. yacht_sections repeater field'ını parse et (Diğer Açıklamalar / Overview – panelden düzenlenebilir)
        const yachtSections = metas['yacht_sections'];
        if (yachtSections && Array.isArray(yachtSections) && yachtSections.length > 0) {
            yachtSections.forEach((section: any, index: number) => {
                const title = section.title || section.name || `Section ${index + 1}`;
                const content = section.content || section.description || '';

                if (!hasMeaningfulHtml(content)) {
                    return;
                }

                const sectionId = slugifySectionId(title, `overview-${index}`);

                result.push({
                    tabName: title,
                    sectionId,
                    groupKey: OTHER_DESCRIPTIONS_GROUP_KEY,
                    fields: [{
                        fieldKey: 'yacht_sections',
                        fieldTitle: title,
                        content: typeof content === 'string' ? content : String(content),
                    }],
                });
            });
        }

        // 2. Rich text field'ları tab_group_key ile grupla (yeni yapı)
        const tabGroups: Record<string, Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
            tabDisplayOrder: number;
        }>> = {};

        // Tüm field'ları dolaş
        Object.entries(fields).forEach(([fieldKey, field]: [string, any]) => {
            // yacht_tabs, yacht_sections ve cancellation_terms ayrı işlendi
            if (fieldKey === 'yacht_tabs' || fieldKey === 'yacht_sections' || fieldKey === CANCELLATION_TERMS_FIELD_KEY) {
                return;
            }

            // included_services ve excluded_services rich_text field'ları services section'ında gösterilecek
            // Bu yüzden tabFields'a eklenmesinler, ayrı bir services section'ı var (parseTabFields için)
            // Ama services tab_group_key'ine ait diğer rich_text field'lar gösterilebilir
            if (fieldKey === 'included_services' || fieldKey === 'excluded_services') {
                return;
            }

            // Sadece rich_text field'ları ve has_data = true olanları al
            if (field.field_type !== 'rich_text' || !field.has_data) {
                return;
            }

            // Meta'dan değeri al
            const value = metas[fieldKey];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                return;
            }

            // Tab group key'i al (yeni yapı) veya tab_name'den oluştur (eski yapı uyumluluğu)
            const tabGroupKey = field.tab_group_key || field.tab_name || 'general';
            
            // Tab name'i al (görüntüleme için)
            const tabName = field.tab_name || field.field_title || 'General';
            
            // Field title'ı dil bazlı al
            let fieldTitle = fieldKey;
            if (field.field_title) {
                fieldTitle = field.field_title;
            }

            // Tab display order'ı al
            const tabDisplayOrder = field.tab_display_order ?? field.sort_order ?? 999;

            // Tab group'a ekle
            if (!tabGroups[tabGroupKey]) {
                tabGroups[tabGroupKey] = [];
            }

            tabGroups[tabGroupKey].push({
                fieldKey,
                fieldTitle,
                content: typeof value === 'string' ? value : String(value),
                tabDisplayOrder,
            });
        });

        // Tab groups'u section ID'ler ile birlikte ekle
        Object.entries(tabGroups).forEach(([tabGroupKey, tabFields]) => {
            tabFields.sort((a, b) => a.tabDisplayOrder - b.tabDisplayOrder);

            // Overview / diğer açıklamalar: her alan ayrı section
            if (tabGroupKey === OTHER_DESCRIPTIONS_GROUP_KEY || tabGroupKey === 'overview') {
                tabFields.forEach((f) => {
                    const fieldDef = fields[f.fieldKey];
                    const sectionId = fieldDef?.tab_section_id
                        || slugifySectionId(f.fieldTitle, f.fieldKey);
                    result.push({
                        tabName: f.fieldTitle,
                        sectionId,
                        groupKey: OTHER_DESCRIPTIONS_GROUP_KEY,
                        fields: [{
                            fieldKey: f.fieldKey,
                            fieldTitle: f.fieldTitle,
                            content: f.content,
                        }],
                    });
                });
                return;
            }

            const firstField = fields[tabFields[0]?.fieldKey];
            const sectionId = firstField?.tab_section_id || 
                             firstField?.menu_section_id || 
                             tabGroupKey.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            
            const tabName = firstField?.tab_name || 
                          firstField?.field_title || 
                          tabGroupKey;

            result.push({
                tabName,
                sectionId,
                fields: tabFields.map(f => ({
                    fieldKey: f.fieldKey,
                    fieldTitle: f.fieldTitle,
                    content: f.content,
                })),
            });
        });

        // Sort order'a göre sırala (tab_display_order veya sort_order kullan)
        return result.sort((a, b) => {
            const aFirstField = a.fields[0];
            const bFirstField = b.fields[0];
            const aField = aFirstField ? fields[aFirstField.fieldKey] : null;
            const bField = bFirstField ? fields[bFirstField.fieldKey] : null;
            const aSort = aField?.tab_display_order ?? aField?.sort_order ?? 999;
            const bSort = bField?.tab_display_order ?? bField?.sort_order ?? 999;
            return aSort - bSort;
        });
    }
}

